import { Controller } from "@hotwired/stimulus";

// Cable carries invalidations only; authenticated requests render each changed row.
export default class extends Controller {
  static values = { url: String };
  static targets = ["frame", "source", "status", "table", "empty"];

  connect() {
    this.active = true;
    this.pendingForms = new Set();
    this.retry = () => this.schedule();
    window.addEventListener("online", this.retry);
    this.schedule();
  }

  disconnect() {
    this.active = false;
    clearTimeout(this.timer);
    this.request?.abort();
    this.observer?.disconnect();
    window.removeEventListener("online", this.retry);
  }

  sourceTargetConnected(source) {
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => this.connectionChanged());
    this.observer.observe(source, { attributes: true, attributeFilter: ["connected"] });
    this.connectionChanged();
  }

  connectionChanged() {
    this.setStatus(this.connected ? "connecting" : "paused");
    if (this.connected) this.schedule();
  }

  get connected() {
    return this.hasSourceTarget && this.sourceTarget.hasAttribute("connected");
  }

  setStatus(state) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.textContent = this.statusTarget.dataset[`liveServices${state[0].toUpperCase()}${state.slice(1)}Value`];
    this.statusTarget.parentElement.dataset.state = state;
  }

  schedule() {
    this.dirty = true;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.refresh(), 180);
  }

  submitting(event) {
    this.pendingForms.add(event.target);
  }

  submitted(event) {
    this.pendingForms.delete(event.target);
    this.schedule();
  }

  get url() {
    return new URL(this.frameTarget.getAttribute("src") || this.urlValue, window.location.origin);
  }

  async refresh() {
    if (!this.active || this.running || this.frameTarget.hasAttribute("busy")) return;
    this.running = true;
    this.dirty = false;
    this.request = new AbortController();
    const url = this.url;
    try {
      const response = await this.fetch(url, "application/json");
      const services = await response.json();
      if (!this.current(url)) return;
      await this.reconcile(services, url);
      this.setStatus(this.connected ? "live" : "paused");
    } catch (error) {
      if (error.name !== "AbortError") {
        this.setStatus("paused");
        this.timer = setTimeout(() => this.schedule(), 5000);
      }
    } finally {
      this.running = false;
      if (this.active && this.dirty) this.schedule();
    }
  }

  async fetch(url, accept) {
    const response = await fetch(url, {
      headers: { Accept: accept }, credentials: "same-origin", cache: "no-store", signal: this.request.signal,
    });
    if (!response.ok || response.redirected) throw new Error("Live services request failed");
    return response;
  }

  current(url) {
    return this.active && !this.frameTarget.hasAttribute("busy") && this.url.href === url.href;
  }

  rows() {
    return [...this.element.querySelectorAll("[data-service-id]")];
  }

  row(id) {
    return this.element.querySelector(`[data-service-id="${Number(id)}"]`);
  }

  protected(row) {
    if (!row) return false;
    return row.contains(document.activeElement) || [...this.pendingForms].some((form) => row.contains(form));
  }

  async reconcile(services, url) {
    const ids = new Set(services.map((service) => String(service.id)));
    this.rows().forEach((row) => {
      if (!ids.has(row.dataset.serviceId) && !this.protected(row)) row.remove();
    });
    for (const service of services) {
      if (!this.current(url)) return;
      const row = this.row(service.id);
      if (row?.dataset.serviceVersion !== service.version && !this.protected(row)) {
        await this.update(service, url);
      }
    }
    if (!this.current(url)) return;
    this.sort(services);
    const empty = !this.element.querySelector("#pending_ordered_services_table [data-service-id]");
    this.tableTarget.hidden = empty;
    this.emptyTarget.hidden = !empty;
    this.element.dispatchEvent(new CustomEvent("live-services:updated", { bubbles: true }));
  }

  async update(service, url) {
    const itemUrl = new URL(url);
    itemUrl.pathname = `${itemUrl.pathname.replace(/\/$/, "")}/${service.id}`;
    const response = await this.fetch(itemUrl, "text/html");
    const html = await response.text();
    if (!this.current(url)) return;
    const old = this.row(service.id);
    if (this.protected(old)) return;
    if (response.status === 204) { old?.remove(); return; }
    const template = document.createElement("template");
    template.innerHTML = html.trim().startsWith("<tr") ? `<table><tbody>${html}</tbody></table>` : html;
    const row = template.content.querySelector("[data-service-id]");
    if (!row || row.dataset.serviceId !== String(service.id)) throw new Error("Invalid service response");
    const selected = old?.querySelector("input[type=checkbox]")?.checked;
    const checkbox = row.querySelector("input[type=checkbox]");
    if (checkbox && !checkbox.disabled) checkbox.checked = Boolean(selected);
    const destination = row.tagName === "TR" ? "pending_ordered_services_table" : "in_progress_ordered_services";
    old?.remove();
    this.element.querySelector(`#${destination}`).append(row);
    if ((!old || old.tagName !== row.tagName) && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      row.animate([{ opacity: 0, transform: "translateY(8px)" }, { opacity: 1, transform: "translateY(0)" }],
        { duration: 240, easing: "ease-out" });
    }
  }

  sort(services) {
    // Move only out-of-order nodes; untouched timers and inputs retain their state.
    const previous = new Map();
    services.forEach(({ id }) => {
      const row = this.row(id);
      if (!row || this.protected(row)) return;
      const parent = row.parentElement;
      const before = previous.has(parent) ? previous.get(parent).nextElementSibling : parent.firstElementChild;
      if (before !== row) parent.insertBefore(row, before);
      previous.set(parent, row);
    });
  }
}
